
import React from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { 
  MapPin,
  ShieldCheck,
  FileText,
  Users,
  Hammer
} from "lucide-react";

interface DeliverableProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  index: number;
}

const Deliverable = ({ icon, title, description, index }: DeliverableProps) => {
  return (
    <div className="flex items-start gap-4 md:gap-6 mb-8 relative">
      {index > 0 && (
        <div className="hidden md:block absolute -top-12 left-6 w-0.5 h-12 bg-gradient-to-b from-transparent to-blue-500/50" />
      )}
      <div className="bg-blue-600/20 text-blue-400 p-3 rounded-xl flex items-center justify-center flex-shrink-0">
        {icon}
      </div>
      <div>
        <h3 className="text-xl font-semibold mb-2 text-white">{title}</h3>
        <p className="text-white/70">{description}</p>
      </div>
    </div>
  );
};

const StrategyDeliverables = () => {
  const deliverables = [
    {
      icon: <MapPin className="h-6 w-6" />,
      title: "Custom AI Roadmap",
      description: "A clear, prioritized action plan tailored to your firm's practice areas, goals, and risk profile — so you can adopt AI with confidence and clarity."
    },
    {
      icon: <ShieldCheck className="h-6 w-6" />,
      title: "Risk Management Framework",
      description: "Built-in safeguards to help you protect client trust, maintain ethical standards, and minimize exposure to emerging AI-related risks."
    },
    {
      icon: <FileText className="h-6 w-6" />,
      title: "Policy Recommendations",
      description: "Practical AI usage policies — designed specifically for legal teams — to ensure safe, compliant, and consistent use across your firm."
    },
    {
      icon: <Users className="h-6 w-6" />,
      title: "Change Management Strategy",
      description: "A strategy to align leadership, attorneys, and staff around AI adoption — minimizing resistance and accelerating firm-wide transformation."
    },
    {
      icon: <Hammer className="h-6 w-6" />,
      title: "Implementation Guidance",
      description: "Step-by-step support to integrate AI tools into your workflows efficiently — so your team sees results faster with minimal disruption."
    }
  ];

  return (
    <section className="py-20 px-6 bg-gradient-to-b from-background/90 to-background">
      <div className="container mx-auto max-w-5xl">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            When you're ready to move beyond experimentation, here's exactly what <span className="text-gradient">DocketLabs delivers</span>
          </h2>
          
          <p className="text-lg text-white/80 max-w-3xl mx-auto">
            Every strategy engagement with DocketLabs includes these essential tools — designed to move your firm from uncertainty to leadership.
          </p>
        </div>
        
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 md:p-10">
          {deliverables.map((item, index) => (
            <Deliverable 
              key={index}
              icon={item.icon}
              title={item.title}
              description={item.description}
              index={index}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default StrategyDeliverables;
